import React, { useState, useCallback, useMemo } from 'react'
import ReactFlow, {
  MiniMap,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  addEdge,
  applyEdgeChanges,
  applyNodeChanges,
  Panel,
  useReactFlow,
  ReactFlowProvider,
  Handle,
  Position,
} from 'reactflow'
import 'reactflow/dist/style.css'
import Button from '../components/Button'
import Modal from '../components/ui/Modal'
import { dummyTables, dummyNodes } from '../data/dummyData'

// Custom node types with input/output handles
const CustomNode = ({ data }) => {
  return (
    <div className="px-4 py-3 shadow-lg rounded-lg border-2 border-gray-200 bg-white min-w-[150px] relative">
      {/* Input Handle */}
      <Handle
        type="target"
        position={Position.Left}
        style={{ top: '50%', transform: 'translateY(-50%)' }}
      />
      
      <div className="flex items-center mb-2">
        <span className="text-2xl mr-2">{data.icon}</span>
        <span className="font-semibold text-gray-800 text-sm">{data.label}</span>
      </div>
      <p className="text-xs text-gray-600 mb-1">Type: {data.type}</p>
      {data.description && (
        <p className="text-xs text-gray-500">{data.description}</p>
      )}
      
      {/* Output Handle */}
      <Handle
        type="source"
        position={Position.Right}
        style={{ top: '50%', transform: 'translateY(-50%)' }}
      />
    </div>
  )
}

// Special node for data sources (only output)
const DataSourceNode = ({ data }) => {
  return (
    <div className="px-4 py-3 shadow-lg rounded-lg border-2 border-green-200 bg-green-50 min-w-[150px] relative">
      <div className="flex items-center mb-2">
        <span className="text-2xl mr-2">{data.icon}</span>
        <span className="font-semibold text-gray-800 text-sm">{data.label}</span>
      </div>
      <p className="text-xs text-gray-600 mb-1">Type: {data.type}</p>
      {data.description && (
        <p className="text-xs text-gray-500">{data.description}</p>
      )}
      
      {/* Only Output Handle for data sources */}
      <Handle
        type="source"
        position={Position.Right}
        style={{ top: '50%', transform: 'translateY(-50%)' }}
      />
    </div>
  )
}

// Special node for actions (only input)
const ActionNode = ({ data }) => {
  return (
    <div className="px-4 py-3 shadow-lg rounded-lg border-2 border-red-200 bg-red-50 min-w-[150px] relative">
      {/* Only Input Handle for action nodes */}
      <Handle
        type="target"
        position={Position.Left}
        style={{ top: '50%', transform: 'translateY(-50%)' }}
      />
      
      <div className="flex items-center mb-2">
        <span className="text-2xl mr-2">{data.icon}</span>
        <span className="font-semibold text-gray-800 text-sm">{data.label}</span>
      </div>
      <p className="text-xs text-gray-600 mb-1">Type: {data.type}</p>
      {data.description && (
        <p className="text-xs text-gray-500">{data.description}</p>
      )}
    </div>
  )
}

const nodeTypes = {
  custom: CustomNode,
  source: DataSourceNode,
  action: ActionNode,
}

// Control Panel Component that can access ReactFlow context
const ControlPanel = ({ onRunFlow, onClearAll, onAutoLayout, nodeCount, edgeCount }) => {
  const { fitView } = useReactFlow()
  
  const handleAutoLayout = () => {
    onAutoLayout()
    setTimeout(() => fitView(), 100)
  }
  
  return (
    <Panel position="top-left" className="bg-white p-4 rounded-lg shadow-lg">
      <div className="flex space-x-2">
        <Button onClick={onRunFlow} className="bg-success-600 hover:bg-success-700 text-white">
          🚀 Run Flow
        </Button>
        <Button onClick={onClearAll} className="bg-red-600 hover:bg-red-700 text-white">
          🗑️ Clear All
        </Button>
        <Button onClick={handleAutoLayout} className="bg-blue-600 hover:bg-blue-700 text-white">
          📐 Auto Layout
        </Button>
      </div>
      <div className="mt-2 text-sm text-gray-600">
        Nodes: {nodeCount} | Connections: {edgeCount}
      </div>
    </Panel>
  )
}

// Workflow List View Component
const WorkflowListView = ({ workflows, onSelectWorkflow, onCreateNew }) => {
  return (
    <div className="flex h-full bg-gray-50">
      <div className="flex-1 p-8">
        <div className="max-w-4xl mx-auto">
          <div className="flex justify-between items-center mb-8">
            <h1 className="text-3xl font-bold text-gray-900">Workflows</h1>
            <Button 
              onClick={onCreateNew}
              className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-3"
            >
              ➕ Create New Workflow
            </Button>
          </div>
          
          {workflows.length === 0 ? (
            <div className="text-center py-16">
              <div className="text-6xl mb-4">🔄</div>
              <h3 className="text-xl font-semibold text-gray-700 mb-2">No workflows yet</h3>
              <p className="text-gray-500 mb-6">Create your first workflow to get started</p>
              <Button 
                onClick={onCreateNew}
                className="bg-primary-600 hover:bg-primary-700 text-white"
              >
                Create Your First Workflow
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {workflows.map((workflow) => (
                <div
                  key={workflow.id}
                  onClick={() => onSelectWorkflow(workflow)}
                  className="bg-white rounded-lg shadow-sm hover:shadow-md transition-all duration-200 cursor-pointer border border-gray-200 p-6"
                >
                  <div className="flex items-center mb-4">
                    <span className="text-3xl mr-3">{workflow.icon}</span>
                    <div>
                      <h3 className="font-semibold text-gray-800">{workflow.name}</h3>
                      <p className="text-sm text-gray-500">{workflow.description}</p>
                    </div>
                  </div>
                  
                  <div className="space-y-2 text-sm text-gray-600">
                    <div className="flex justify-between">
                      <span>Nodes:</span>
                      <span className="font-medium">{workflow.nodeCount}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Last Modified:</span>
                      <span className="font-medium">{workflow.lastModified}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Status:</span>
                      <span className={`font-medium ${
                        workflow.status === 'active' ? 'text-green-600' : 
                        workflow.status === 'draft' ? 'text-yellow-600' : 'text-gray-600'
                      }`}>
                        {workflow.status}
                      </span>
                    </div>
                  </div>
                  
                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <div className="flex space-x-2">
                      <Button 
                        onClick={(e) => {
                          e.stopPropagation()
                          onSelectWorkflow(workflow)
                        }}
                        className="flex-1 bg-primary-600 hover:bg-primary-700 text-white text-sm"
                      >
                        ✏️ Edit
                      </Button>
                      <Button 
                        onClick={(e) => {
                          e.stopPropagation()
                          // TODO: Implement duplicate workflow
                        }}
                        className="flex-1 bg-gray-600 hover:bg-gray-700 text-white text-sm"
                      >
                        📋 Duplicate
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// Workflow Editor Component
const WorkflowEditor = ({ workflow, onSave, onBack, onDelete }) => {
  const [nodes, setNodes, onNodesChange] = useNodesState(workflow?.nodes || [])
  const [edges, setEdges, onEdgesChange] = useEdgesState(workflow?.edges || [])
  const [modalMessage, setModalMessage] = useState(null)
  const [workflowName, setWorkflowName] = useState(workflow?.name || '')
  const [isEditingName, setIsEditingName] = useState(!workflow?.name)
  const [activePanel, setActivePanel] = useState('nodes') // 'nodes' or 'chat' or null
  const [chatMessages, setChatMessages] = useState([])
  const [chatInput, setChatInput] = useState('')

  // Handle edge connections
  const onConnect = useCallback(
    (params) => setEdges((eds) => addEdge(params, eds)),
    [setEdges]
  )

  // Handle node changes (position, etc.)
  const onNodesChangeCallback = useCallback(
    (changes) => onNodesChange(changes),
    [onNodesChange]
  )

  // Handle edge changes
  const onEdgesChangeCallback = useCallback(
    (changes) => onEdgesChange(changes),
    [onEdgesChange]
  )

  // Drag and drop handlers
  const onDragStart = (event, nodeType, nodeData) => {
    event.dataTransfer.setData('application/reactflow', JSON.stringify({ type: nodeType, data: nodeData }))
    event.dataTransfer.effectAllowed = 'move'
  }

  const onDragOver = useCallback((event) => {
    event.preventDefault()
    event.dataTransfer.dropEffect = 'move'
  }, [])

  const onDrop = useCallback(
    (event) => {
      event.preventDefault()

      const reactFlowBounds = document.querySelector('.react-flow').getBoundingClientRect()
      const data = JSON.parse(event.dataTransfer.getData('application/reactflow'))

      const position = {
        x: event.clientX - reactFlowBounds.left,
        y: event.clientY - reactFlowBounds.top,
      }

      // Determine node type based on data type
      let nodeType = 'custom'
      if (data.data.type === 'source') {
        nodeType = 'source'
      } else if (data.data.type === 'action') {
        nodeType = 'action'
      }

      const newNode = {
        id: `${data.type}-${Date.now()}`,
        type: nodeType,
        position,
        data: {
          label: data.data.name,
          icon: data.data.icon,
          type: data.data.type,
          description: data.data.description || '',
        },
      }

      setNodes((nds) => nds.concat(newNode))
    },
    [setNodes]
  )

  // Save workflow
  const handleSave = () => {
    if (!workflowName.trim()) {
      setModalMessage('Please enter a workflow name!')
      return
    }

    const workflowData = {
      id: workflow?.id || `workflow-${Date.now()}`,
      name: workflowName,
      description: workflow?.description || 'Workflow description',
      icon: workflow?.icon || '🔄',
      nodes: nodes,
      edges: edges,
      nodeCount: nodes.length,
      lastModified: new Date().toLocaleDateString(),
      status: 'active',
      createdAt: workflow?.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString()
    }

    onSave(workflowData)
    setModalMessage('✅ Workflow saved successfully!')
  }

  // Run flow simulation
  const handleRunFlow = () => {
    if (nodes.length === 0) {
      setModalMessage('Please add some nodes to the flow first!')
      return
    }

    let simulationMessage = '🚀 Initiating flow simulation...\n\n'
    const visited = new Set()
    const nodeMap = new Map(nodes.map(node => [node.id, node]))

    const processNode = (nodeId) => {
      if (visited.has(nodeId)) return
      visited.add(nodeId)

      const node = nodeMap.get(nodeId)
      if (!node) return

      simulationMessage += `📋 Executing **${node.data.label}** (${node.data.type})...\n`

      // Simulate different node types
      switch (node.data.type) {
        case 'source':
          simulationMessage += '   📥 Fetching data from source...\n'
          break
        case 'process':
          if (node.data.label.includes('Filter')) {
            simulationMessage += '   🔍 Applying criteria: >50% revenue growth, historical high in last 7 days.\n'
          } else if (node.data.label.includes('Chart')) {
            simulationMessage += '   📈 Creating candlestick charts for filtered stocks.\n'
          } else if (node.data.label.includes('Transform')) {
            simulationMessage += '   ⚙️ Transforming data format...\n'
          }
          break
        case 'ai':
          simulationMessage += '   🧠 Vision LLM analyzing charts for buy/sell signals.\n'
          break
        case 'action':
          if (node.data.label.includes('Email')) {
            simulationMessage += '   📧 Checking for changes in stock list. Email alert triggered!\n'
          } else if (node.data.label.includes('Trade')) {
            simulationMessage += '   🚀 Connecting to TD Ameritrade. Trade placed! (Simulated)\n'
          }
          break
        case 'logic':
          simulationMessage += `   ➡️ Performing ${node.data.label} operation.\n`
          break
        default:
          simulationMessage += '   ⚡ Processing node...\n'
      }

      // Process connected nodes
      edges
        .filter(edge => edge.source === nodeId)
        .forEach(edge => processNode(edge.target))
    }

    // Start from nodes with no incoming edges (sources)
    const sourceNodes = nodes.filter(node => 
      !edges.some(edge => edge.target === node.id)
    )

    if (sourceNodes.length === 0) {
      // If no clear sources, start from all nodes
      nodes.forEach(node => processNode(node.id))
    } else {
      sourceNodes.forEach(node => processNode(node.id))
    }

    simulationMessage += '\n✅ Flow simulation complete!'
    setModalMessage(simulationMessage)
  }

  // Clear all nodes and edges
  const handleClearAll = () => {
    setNodes([])
    setEdges([])
  }

  // Auto-layout nodes
  const handleAutoLayout = () => {
    const newNodes = nodes.map((node, index) => ({
      ...node,
      position: {
        x: 50 + (index % 3) * 250,
        y: 50 + Math.floor(index / 3) * 150,
      },
    }))
    setNodes(newNodes)
  }

  // Enhanced node data with descriptions
  const enhancedNodes = useMemo(() => [
    ...dummyNodes.map(node => ({
      ...node,
      description: getNodeDescription(node)
    })),
    ...dummyTables.map(table => ({
      ...table,
      type: 'source',
      description: `Data source: ${table.name}`
    }))
  ], [])

  function getNodeDescription(node) {
    switch (node.type) {
      case 'source':
        return 'Data input node'
      case 'process':
        return 'Data processing operation'
      case 'ai':
        return 'AI/ML analysis node'
      case 'action':
        return 'Action execution node'
      case 'logic':
        return 'Logical operation node'
      default:
        return 'Custom node'
    }
  }

  // Panel toggle functions
  const toggleNodesPanel = () => {
    setActivePanel(activePanel === 'nodes' ? null : 'nodes')
  }

  const toggleChatPanel = () => {
    setActivePanel(activePanel === 'chat' ? null : 'chat')
  }

  // Chat functions
  const handleChatSubmit = (e) => {
    e.preventDefault()
    if (!chatInput.trim()) return

    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: chatInput,
      timestamp: new Date()
    }

    setChatMessages([...chatMessages, userMessage])
    setChatInput('')

    // Simulate AI response
    setTimeout(() => {
      const aiMessage = {
        id: Date.now() + 1,
        type: 'ai',
        content: `I can help you with your workflow! You have ${nodes.length} nodes and ${edges.length} connections. What would you like to do?`,
        timestamp: new Date()
      }
      setChatMessages(prev => [...prev, aiMessage])
    }, 1000)
  }

  return (
    <ReactFlowProvider>
      <div className="flex h-full bg-gray-50">
        {/* Header */}
        <div className="absolute top-0 left-0 right-0 z-10 bg-white border-b border-gray-200 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button 
                onClick={onBack}
                className="bg-gray-600 hover:bg-gray-700 text-white"
              >
                ← Back to Workflows
              </Button>
              
              {isEditingName ? (
                <div className="flex items-center space-x-2">
                  <input
                    type="text"
                    value={workflowName}
                    onChange={(e) => setWorkflowName(e.target.value)}
                    placeholder="Enter workflow name..."
                    className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    autoFocus
                  />
                  <Button 
                    onClick={() => setIsEditingName(false)}
                    className="bg-green-600 hover:bg-green-700 text-white"
                  >
                    ✓
                  </Button>
                </div>
              ) : (
                <div className="flex items-center space-x-2">
                  <h1 className="text-xl font-semibold text-gray-800">{workflowName || 'Untitled Workflow'}</h1>
                  <button 
                    onClick={() => setIsEditingName(true)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    ✏️
                  </button>
                </div>
              )}
            </div>
            
            <div className="flex items-center space-x-2">
              <Button 
                onClick={handleSave}
                className="bg-primary-600 hover:bg-primary-700 text-white"
              >
                💾 Save Workflow
              </Button>
              {workflow?.id && (
                <Button 
                  onClick={() => {
                    if (confirm('Are you sure you want to delete this workflow?')) {
                      onDelete(workflow.id)
                    }
                  }}
                  className="bg-red-600 hover:bg-red-700 text-white"
                >
                  🗑️ Delete
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* React Flow Canvas */}
        <div className="flex-1 relative pt-16">
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChangeCallback}
            onEdgesChange={onEdgesChangeCallback}
            onConnect={onConnect}
            onDragOver={onDragOver}
            onDrop={onDrop}
            nodeTypes={nodeTypes}
            fitView
            className="bg-gray-50"
          >
            <Controls />
            <MiniMap 
              nodeStrokeColor={(n) => {
                if (n.type === 'input') return '#0041d0'
                if (n.type === 'output') return '#ff0072'
                return '#1a192b'
              }}
              nodeColor={(n) => {
                if (n.data?.type === 'source') return '#10b981'
                if (n.data?.type === 'ai') return '#8b5cf6'
                if (n.data?.type === 'action') return '#ef4444'
                return '#6b7280'
              }}
            />
            <Background variant="dots" gap={12} size={1} />
            
            {/* Control Panel */}
            <ControlPanel 
              onRunFlow={handleRunFlow}
              onClearAll={handleClearAll}
              onAutoLayout={handleAutoLayout}
              nodeCount={nodes.length}
              edgeCount={edges.length}
            />
          </ReactFlow>
        </div>

        {/* Panel Toggle Buttons */}
        <div className="absolute right-4 top-20 z-20 flex flex-col space-y-2">
          <button
            onClick={toggleNodesPanel}
            className={`w-12 h-12 rounded-lg shadow-lg flex items-center justify-center transition-all duration-200 ${
              activePanel === 'nodes'
                ? 'bg-primary-600 text-white'
                : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
            }`}
            title="Add nodes"
          >
            <span className="text-xl">➕</span>
          </button>
          <button
            onClick={toggleChatPanel}
            className={`w-12 h-12 rounded-lg shadow-lg flex items-center justify-center transition-all duration-200 ${
              activePanel === 'chat'
                ? 'bg-primary-600 text-white'
                : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
            }`}
            title="AI Assistant"
          >
            <span className="text-xl">🤖</span>
          </button>
        </div>

        {/* Right Panel */}
        {activePanel && (
          <div className="absolute right-0 top-16 bottom-0 w-80 bg-white border-l border-gray-200 shadow-lg z-10">
            {activePanel === 'nodes' && (
              <div className="flex flex-col h-full">
                <div className="p-4 border-b border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">
                    What happens next?
                  </h3>
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Search nodes..."
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
                    />
                    <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
                      🔍
                    </span>
                  </div>
                </div>
                
                <div className="flex-1 overflow-y-auto p-4 custom-scrollbar">
                  <div className="mb-6">
                    <h4 className="text-sm font-semibold text-gray-700 mb-3">📊 Data Sources</h4>
                    <ul className="space-y-2">
                      {dummyTables.map((table) => (
                        <li
                          key={table.id}
                          draggable
                          onDragStart={(e) => onDragStart(e, 'table', table)}
                          className="flex items-center p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-all duration-200 cursor-grab border border-gray-200"
                        >
                          <span className="text-lg mr-3">{table.icon}</span>
                          <div className="flex-1">
                            <span className="font-medium text-gray-700 text-sm block">{table.name}</span>
                            <span className="text-xs text-gray-500">Data source</span>
                          </div>
                          <span className="text-gray-400">→</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  
                  <div className="border-t border-gray-200 pt-6">
                    <h4 className="text-sm font-semibold text-gray-700 mb-3">🔧 Flow Nodes</h4>
                    <ul className="space-y-2">
                      {enhancedNodes.map((node) => (
                        <li
                          key={node.id}
                          draggable
                          onDragStart={(e) => onDragStart(e, 'node', node)}
                          className="flex items-center p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-all duration-200 cursor-grab border border-gray-200"
                        >
                          <span className="text-lg mr-3">{node.icon}</span>
                          <div className="flex-1">
                            <span className="font-medium text-gray-700 text-sm block">{node.name}</span>
                            <span className="text-xs text-gray-500">{node.description}</span>
                          </div>
                          <span className="text-gray-400">→</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            )}

            {activePanel === 'chat' && (
              <div className="flex flex-col h-full">
                <div className="p-4 border-b border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">
                    AI Workflow Assistant
                  </h3>
                  <p className="text-sm text-gray-600">
                    Ask me anything about your workflow
                  </p>
                </div>
                
                <div className="flex-1 overflow-y-auto p-4 custom-scrollbar">
                  {chatMessages.length === 0 ? (
                    <div className="text-center py-8">
                      <div className="text-4xl mb-4">🤖</div>
                      <h4 className="text-lg font-semibold text-gray-700 mb-2">Workflow Assistant</h4>
                      <p className="text-sm text-gray-500">
                        I can help you build, optimize, and troubleshoot your workflows. Ask me anything!
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {chatMessages.map((message) => (
                        <div
                          key={message.id}
                          className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
                        >
                          <div
                            className={`max-w-xs p-3 rounded-lg ${
                              message.type === 'user'
                                ? 'bg-primary-600 text-white'
                                : 'bg-gray-100 text-gray-800'
                            }`}
                          >
                            <p className="text-sm">{message.content}</p>
                            <p className="text-xs opacity-70 mt-1">
                              {message.timestamp.toLocaleTimeString()}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                
                <div className="p-4 border-t border-gray-200">
                  <form onSubmit={handleChatSubmit} className="flex space-x-2">
                    <input
                      type="text"
                      value={chatInput}
                      onChange={(e) => setChatInput(e.target.value)}
                      placeholder="Ask about your workflow..."
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
                    />
                    <button
                      type="submit"
                      className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                    >
                      Send
                    </button>
                  </form>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      
      {modalMessage && (
        <Modal 
          message={modalMessage} 
          onClose={() => setModalMessage(null)} 
        />
      )}
    </ReactFlowProvider>
  )
}

// Main FlowPage Component
const FlowPage = () => {
  const [view, setView] = useState('list') // 'list' or 'editor'
  const [workflows, setWorkflows] = useState([
    {
      id: 'workflow-1',
      name: 'Stock Analysis Pipeline',
      description: 'Automated stock screening and analysis workflow',
      icon: '📈',
      nodeCount: 5,
      lastModified: '2024-01-15',
      status: 'active',
      nodes: [],
      edges: []
    },
    {
      id: 'workflow-2',
      name: 'Portfolio Monitoring',
      description: 'Real-time portfolio tracking and alerts',
      icon: '📊',
      nodeCount: 3,
      lastModified: '2024-01-14',
      status: 'draft',
      nodes: [],
      edges: []
    }
  ])
  const [currentWorkflow, setCurrentWorkflow] = useState(null)

  const handleCreateNew = () => {
    setCurrentWorkflow(null)
    setView('editor')
  }

  const handleSelectWorkflow = (workflow) => {
    setCurrentWorkflow(workflow)
    setView('editor')
  }

  const handleSaveWorkflow = (workflowData) => {
    const existingIndex = workflows.findIndex(w => w.id === workflowData.id)
    
    if (existingIndex >= 0) {
      // Update existing workflow
      const updatedWorkflows = [...workflows]
      updatedWorkflows[existingIndex] = workflowData
      setWorkflows(updatedWorkflows)
    } else {
      // Add new workflow
      setWorkflows([...workflows, workflowData])
    }
    
    setCurrentWorkflow(workflowData)
  }

  const handleDeleteWorkflow = (workflowId) => {
    setWorkflows(workflows.filter(w => w.id !== workflowId))
    setView('list')
  }

  const handleBackToList = () => {
    setView('list')
    setCurrentWorkflow(null)
  }

  return (
    <div className="h-full">
      {view === 'list' ? (
        <WorkflowListView
          workflows={workflows}
          onSelectWorkflow={handleSelectWorkflow}
          onCreateNew={handleCreateNew}
        />
      ) : (
        <WorkflowEditor
          workflow={currentWorkflow}
          onSave={handleSaveWorkflow}
          onBack={handleBackToList}
          onDelete={handleDeleteWorkflow}
        />
      )}
    </div>
  )
}

export default FlowPage 